import {
  websiteDiscoverySchema,
  type WebsiteDiscoveryConfig,
} from '@kanbu/schema/contracts';
import { Trans, useLingui } from '@lingui/react/macro';
import { Input, Label, Select, SelectItem, Switch } from '@utima/ui';
import { Info } from 'lucide-react';
import { useId, type ReactNode } from 'react';

import { SimpleTooltip } from '@/components/tooltips/simple-tooltip';

function Help({ text }: { text: string }) {
  return (
    <SimpleTooltip title={text}>
      <span
        role='img'
        tabIndex={0}
        aria-label={text}
        className='inline-flex align-middle text-muted-fg'
      >
        <Info className='size-3.5' />
      </span>
    </SimpleTooltip>
  );
}

export function WebsiteDiscoveryOptions({
  value,
  onChange,
  mirror,
  onMirrorChange,
  discoveryEnabled,
  mirrorLocked = false,
  children,
}: {
  value: WebsiteDiscoveryConfig;
  onChange: (value: WebsiteDiscoveryConfig) => void;
  mirror: boolean;
  onMirrorChange: (value: boolean) => void;
  discoveryEnabled: boolean;
  mirrorLocked?: boolean;
  children?: ReactNode;
}) {
  const { t } = useLingui();
  const id = useId();
  const patch = (data: Record<string, unknown>) => {
    const parsed = websiteDiscoverySchema.safeParse({ ...value, ...data });
    if (parsed.success) onChange(parsed.data);
  };
  const descriptions = {
    quick: t`Fast URL discovery using the website map. May miss unlisted documents. Content is processed after import.`,
    listing: t`Reads listing pages and collects document links without parsing every file. Each visited listing incurs usage.`,
    crawl: t`Crawls AND scrapes visited resources before selection, including supported documents. Usually costs more than listing discovery. PDF parsing is charged per PDF page. Scheduled scans also incur this usage when Auto Refresh is off. Selected content is processed again during import.`,
    single: t`Imports only this URL. Does not follow links or find attachments. Auto Refresh remains available.`,
  };

  return (
    <details className='rounded-lg bg-muted/30 px-3 py-2'>
      <summary className='cursor-pointer text-xs text-muted-fg'>
        <Trans>Advanced settings</Trans>
      </summary>
      <div className='mt-3 flex flex-col gap-3'>
        {children}
        <div
          className={
            value.method === 'quick'
              ? 'grid grid-cols-1 items-end gap-3 sm:grid-cols-2'
              : 'grid grid-cols-1 items-end gap-3 sm:grid-cols-[2fr_1fr_1fr]'
          }
        >
          <div className='space-y-1'>
            <Label htmlFor={`${id}-method`} className='text-xs'>
              <Trans>Discovery method</Trans>{' '}
              <Help text={descriptions[value.method]} />
            </Label>
            <Select
              id={`${id}-method`}
              value={value.method}
              onValueChange={method =>
                onChange(
                  websiteDiscoverySchema.parse({
                    ...(value.method === 'single'
                      ? {}
                      : {
                          includePaths: value.includePaths,
                          excludePaths: value.excludePaths,
                        }),
                    method,
                  }),
                )
              }
            >
              <SelectItem value='quick'>
                <span className='inline-flex items-center gap-2'>
                  <Trans>Quick website scan</Trans>
                  <Help text={descriptions.quick} />
                </span>
              </SelectItem>
              <SelectItem value='listing'>
                <span className='inline-flex items-center gap-2'>
                  <Trans>Find documents in listings</Trans>
                  <Help text={descriptions.listing} />
                </span>
              </SelectItem>
              <SelectItem value='crawl'>
                <span className='inline-flex items-center gap-2'>
                  <Trans>Follow links and scrape</Trans>
                  <Help text={descriptions.crawl} />
                </span>
              </SelectItem>
              <SelectItem value='single'>
                <span className='inline-flex items-center gap-2'>
                  <Trans>This URL only</Trans>
                  <Help text={descriptions.single} />
                </span>
              </SelectItem>
            </Select>
          </div>
          {value.method !== 'single' && (
            <>
              {value.method !== 'quick' && (
                <label className='flex flex-col gap-1 text-xs'>
                  <span>
                    <Trans>Maximum depth</Trans>{' '}
                    <Help
                      text={t`Depth 0 reads only the starting page. Document listings also collect its document links, but do not open child listings or pagination pages.`}
                    />
                  </span>
                  <Input
                    type='number'
                    min={0}
                    max={100}
                    value={value.maxDepth}
                    onChange={event =>
                      patch({ maxDepth: Number(event.target.value) })
                    }
                  />
                </label>
              )}
              <label className='flex flex-col gap-1 text-xs'>
                {value.method === 'quick' ? (
                  <Trans>Maximum URLs returned</Trans>
                ) : (
                  <Trans>Maximum pages visited</Trans>
                )}
                <Input
                  type='number'
                  min={1}
                  max={value.method === 'quick' ? 100000 : 10000}
                  value={value.limit}
                  onChange={event =>
                    patch({ limit: Number(event.target.value) })
                  }
                />
              </label>
            </>
          )}
        </div>
        {value.method === 'crawl' && (
          <p className='text-xs text-muted-fg'>
            <Trans>Scrapes content during discovery; higher usage cost.</Trans>
          </p>
        )}
        {value.method !== 'single' && (
          <div className='grid grid-cols-1 gap-2 sm:grid-cols-2'>
            <label className='flex flex-col gap-1 text-xs'>
              <span>
                <Trans>Include paths (regex)</Trans>{' '}
                <Help
                  text={t`One regex per line, matched against URL paths. Include the starting page and intermediate listings. Lookaround and backreferences are not supported.`}
                />
              </span>
              <textarea
                className='rounded-lg border border-border bg-background p-2 text-sm'
                rows={1}
                placeholder='^/documents/'
                value={value.includePaths.join('\n')}
                onChange={event =>
                  patch({ includePaths: event.target.value.split('\n') })
                }
              />
            </label>
            <label className='flex flex-col gap-1 text-xs'>
              <span>
                <Trans>Exclude paths (regex)</Trans>{' '}
                <Help
                  text={t`One regex per line. Exclusions take priority and apply before following links. Lookaround and backreferences are not supported.`}
                />
              </span>
              <textarea
                className='rounded-lg border border-border bg-background p-2 text-sm'
                rows={1}
                placeholder='^/documents/archive/'
                value={value.excludePaths.join('\n')}
                onChange={event =>
                  patch({ excludePaths: event.target.value.split('\n') })
                }
              />
            </label>
          </div>
        )}
        <label className='flex items-center gap-2 text-xs'>
          <Switch
            checked={mirror}
            disabled={
              mirrorLocked || discoveryEnabled || value.method === 'single'
            }
            onCheckedChange={onMirrorChange}
          />
          <Trans>Mirror website hierarchy</Trans>{' '}
          <Help
            text={
              discoveryEnabled
                ? t`Required when looking for new sources. New sources use the mirrored folder structure.`
                : t`Organize imported sources into folders matching the website URL paths.`
            }
          />
        </label>
      </div>
    </details>
  );
}
